/* ============================================================================
   M0 — Clickpost Setup (Credentials)   route /supply/credentials
   Per-courier API / label / AWB credentials per account code.
   Field set changes by courier partner (forward vs reverse) — 14 profiles.
   Verbatim port of renderCredentials() from proship-supply-side.html §4138.
   ========================================================================== */
import React, {useState, useMemo} from 'react'
import {useSearchParams} from 'react-router-dom'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import CheckIcon from '@mui/icons-material/Check'
import PageTitle from '../../../components/PageTitle'
import {PageHeader, SearchSelect, useToast} from '../components'
import {DUMMY_CODES} from '../dummyData'
import {SCHEMAS, isRev, schemaKey} from './credentialSchemas'

/* Single-admin persona — always treat as admin (no RBAC gating needed). */
const IS_ADMIN = true

/* ─── Empty state ─────────────────────────────────────────────────────────── */
function EmptyState() {
  return (
    <div className='empty'>
      <div className='ic'>
        <BuildOutlinedIcon style={{width: 30, height: 30}} />
      </div>
      <h3>Select a courier account code</h3>
      <p>Pick an account code to configure its ProShip credentials. The field set changes based on the courier partner.</p>
    </div>
  )
}

/* ─── Single text / select field ─────────────────────────────────────────── */
function CredField({field, value, onChange}) {
  const [label, type, opts] = field
  const id = 'cr_' + label.replace(/[^A-Za-z0-9]+/g, '_')

  if (type === 'sel') {
    return (
      <div className='field'>
        <label>
          {label} <span className='opt'>(optional)</span>
        </label>
        <SearchSelect
          value={value || ''}
          placeholder='Search…'
          options={opts || []}
          onChange={(v) => onChange(label, v)}
        />
      </div>
    )
  }

  return (
    <div className='field'>
      <label htmlFor={id}>
        {label} <span className='opt'>(optional)</span>
      </label>
      <input
        className='input'
        id={id}
        value={value || ''}
        onChange={(e) => onChange(label, e.target.value)}
      />
    </div>
  )
}

/* ─── Toggle flag row ─────────────────────────────────────────────────────── */
function TogRow({field, value, onToggle}) {
  const [label] = field
  const on = !!value
  return (
    <div
      onClick={() => onToggle(label)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '10px 12px',
        cursor: 'pointer',
      }}
    >
      <span style={{fontSize: 12.5, fontWeight: 600}}>{label}</span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: on ? '#fff' : 'var(--neutral)',
          background: on ? 'var(--primary)' : 'var(--row-alt)',
          border: '1px solid ' + (on ? 'var(--primary)' : 'var(--border)'),
          borderRadius: 999,
          padding: '2px 10px',
        }}
      >
        {on ? 'YES' : 'NO'}
      </span>
    </div>
  )
}

/* ─── Credential card — shown when a code is selected ────────────────────── */
function CredCard({codeObj, data, onFieldChange, onToggle, onTest, onSave}) {
  const sk = schemaKey(codeObj)
  const sch = SCHEMAS[sk] || SCHEMAS.GENERIC
  const reverse = isRev(codeObj)

  const inputFields = useMemo(() => sch.fields.filter((f) => f[1] !== 'tog'), [sch])
  const togFields = useMemo(() => sch.fields.filter((f) => f[1] === 'tog'), [sch])

  return (
    <div className='card card-pad'>
      {/* Header */}
      <div className='row between center wrap gap10' style={{marginBottom: 4}}>
        <h3 style={{margin: 0}}>Credentials — Account Settings</h3>
        <span className='badge b-info'>{sch.label}</span>
      </div>

      <p className='muted' style={{fontSize: 13, margin: '0 0 14px'}}>
        Fields match the live Clickpost setup for{' '}
        <b>{codeObj.parent || 'this courier'}</b>
        {reverse ? ' (reverse)' : ''} — account code{' '}
        <b className='mono'>{codeObj.code}</b>.
      </p>

      {/* Input fields — 3-column grid (mockup) */}
      <div
        className='grid'
        style={{gridTemplateColumns: 'repeat(3, 1fr)', gap: 14}}
      >
        {inputFields.map((f) => (
          <CredField
            key={f[0]}
            field={f}
            value={data[f[0]]}
            onChange={onFieldChange}
          />
        ))}
      </div>

      {/* Toggle flags */}
      {togFields.length > 0 && (
        <>
          <div className='seclabel' style={{margin: '18px 0 8px'}}>
            Flags
          </div>
          <div
            className='grid'
            style={{gridTemplateColumns: 'repeat(3, 1fr)', gap: 10}}
          >
            {togFields.map((f) => (
              <TogRow
                key={f[0]}
                field={f}
                value={data[f[0]]}
                onToggle={onToggle}
              />
            ))}
          </div>
        </>
      )}

      {/* Footer buttons */}
      <div
        className='row gap12'
        style={{marginTop: 18, justifyContent: 'flex-end'}}
      >
        {IS_ADMIN ? (
          <>
            <button className='btn' onClick={onTest}>
              <BoltOutlinedIcon style={{width: 15, height: 15}} />
              Test connection
            </button>
            <button className='btn btn-primary' onClick={onSave}>
              <CheckIcon style={{width: 16, height: 16}} />
              Save credentials
            </button>
          </>
        ) : (
          <span className='muted' style={{fontSize: 12}}>
            Read-only — only ProShip admins can edit credentials.
          </span>
        )}
      </div>
    </div>
  )
}

/* ─── Page ────────────────────────────────────────────────────────────────── */
export default function ClickpostSetup() {
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  /* Selected account code (code string, not object) — seed from ?code= param */
  const [selectedCode, setSelectedCode] = useState(() => params.get('code') || '')

  /* Per-field values keyed by field label — reset when the code changes */
  const [fieldData, setFieldData] = useState({})

  const handleCodeChange = (val) => {
    setSelectedCode(val)
    setFieldData({})
    const next = new URLSearchParams(params)
    if (val) next.set('code', val)
    else next.delete('code')
    setParams(next, {replace: true})
  }

  const handleFieldChange = (label, value) => {
    setFieldData((prev) => ({...prev, [label]: value}))
  }

  const handleToggle = (label) => {
    setFieldData((prev) => ({...prev, [label]: !prev[label]}))
  }

  /* Resolve the selected code object — mockup falls back to {} (GENERIC schema)
     when the ?code= deep-link does not match any known account code */
  const codeObj = useMemo(
    () =>
      DUMMY_CODES.find((c) => c.code === selectedCode) || {code: selectedCode},
    [selectedCode]
  )

  const sch = SCHEMAS[schemaKey(codeObj)] || SCHEMAS.GENERIC

  const handleTest = () => {
    toast('Test ping sent — connection OK (mock).', 'success')
  }

  const handleSave = () => {
    toast(`Credentials saved for ${selectedCode} (${sch.label}).`, 'success')
  }

  return (
    <>
      <PageTitle>Clickpost Setup (Credentials)</PageTitle>

      <PageHeader
        title='Clickpost Setup (Credentials)'
        sub='Configure how ProShip connects to each courier account code via Clickpost. Fields vary by courier partner (forward / reverse) to mirror the live Clickpost setup. Manual entry; no auto-extraction.'
      />

      {/* Code selector card */}
      <div className='card card-pad' style={{maxWidth: 440, marginBottom: 14}}>
        <div className='field' style={{margin: 0}}>
          <label>
            Courier account code <span className='req'>*</span>
          </label>
          <SearchSelect
            value={selectedCode}
            placeholder='Search an account code…'
            options={DUMMY_CODES.map((c) => ({value: c.code, label: (c.display || c.code) + ' · ' + c.parent}))}
            onChange={handleCodeChange}
          />
        </div>
      </div>

      {/* Body — empty state or credential card */}
      {!selectedCode ? (
        <EmptyState />
      ) : (
        <CredCard
          codeObj={codeObj}
          data={fieldData}
          onFieldChange={handleFieldChange}
          onToggle={handleToggle}
          onTest={handleTest}
          onSave={handleSave}
        />
      )}
    </>
  )
}
